import { useCallback, useEffect, useRef, useState } from 'react'
import { toastNotificationStore } from '@/components/ANewDesign/ToastNotification/ToastNotification'

type UseAsyncMutationOptions<TResult> = {
  /** User-facing error message override. Defaults to `err.message` or a generic string. */
  errorMessage?: string
  /** Side-effect to run on success (after pending clears). */
  onSuccess?: (result: TResult) => void
  /** Side-effect to run on failure. The toast is shown regardless. */
  onError?: (err: unknown) => void
}

/**
 * Wraps an async mutation handler to track in-flight state and surface errors
 * via the toast queue. Used by tournament screens (lobby/HUD/prompts/create
 * form) to make every host mutation behave consistently:
 *  - Single source of truth for the disabled flag (`pending`).
 *  - Toast on error so the user knows what happened (replaces silent
 *    `console.error` patterns that don't render on native).
 *  - Idempotent: a tap while pending is dropped rather than enqueued.
 *
 * The wrapped function preserves the original return value so callers can
 * decide what to do on success (e.g. close a modal).
 *
 * ## Stability contract
 *
 * `run` returned from this hook MUST keep the same identity across renders
 * (the `pending` flag is the only state that affects the caller's tree, and
 * it's already a stable React state value). Reasons:
 *  - Consumers wrap the result in `useCallback` for their own memoization.
 *    A changing `run` identity invalidates every dependent callback in the
 *    tree, cascading into list-row remounts, FlatList tear-downs, etc.
 *  - Putting `pending` in the dep array previously busted memoization twice
 *    per call (false→true on entry, true→false on exit) — the chat send
 *    button and player drawer were re-rendering on every send.
 *
 * Implementation: the closure reads `pending` from `pendingRef.current` so
 * the guard is correct without being a dep; the `options` object is mirrored
 * to a ref so consumers don't have to memoize their options.
 */
export function useAsyncMutation<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
  options?: UseAsyncMutationOptions<TResult>
) {
  const [pending, setPending] = useState(false)
  const pendingRef = useRef(false)
  const fnRef = useRef(fn)
  const optionsRef = useRef(options)

  useEffect(() => {
    fnRef.current = fn
    optionsRef.current = options
  })

  const run = useCallback(
    async (...args: TArgs): Promise<TResult | undefined> => {
      if (pendingRef.current) return undefined
      pendingRef.current = true
      setPending(true)
      try {
        const result = await fnRef.current(...args)
        optionsRef.current?.onSuccess?.(result)
        return result
      } catch (err) {
        const message =
          optionsRef.current?.errorMessage ??
          (err instanceof Error ? err.message : 'Something went wrong. Please try again.')
        // `show()` enqueues — two rapid failures from the same caller now
        // both render (was: second overwrote the first).
        toastNotificationStore.getState().show({ text: message })
        optionsRef.current?.onError?.(err)
        return undefined
      } finally {
        pendingRef.current = false
        setPending(false)
      }
    },
    []
  )

  return { run, pending } as const
}
